import ModalTittle from "@View/components/ModalTittle";
import ModalWrapper from "@View/components/ModalWrapper";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Tabs, Tab, Box } from "@mui/material";
import ButtonSave from "@View/components/buttons/ButtonSave";
import ButtonBB from "@View/components/buttons/ButtonBB";
import getAPI from "@API/index";
import { getJsonViaWebSocket } from "@Utils/websocketDownloader";
import { getRandomKey } from "@Utils/math";
import { AppActions } from "@State/reducers/getReducers/appReducer";
import CreditPolicyTab from "./SDMComponents/CreditPolicyTab";
import ApplicationTab from "./SDMComponents/ApplicationTab";
import GeneralInfoTab from "./SDMComponents/GeneralInfoTab";
import TurnoverTab from "./SDMComponents/TurnoverTab";
import LoansTab from "./SDMComponents/LoansTab";
import DepositsTab from "./SDMComponents/DepositsTab";
import CompanyTab from "./SDMComponents/CompanyTab";


const SDMClientCreditOpinion = ({ handleClose, clientId, clientName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activityTypes, setActivityTypes] = useState([]);
  const [formData, setFormData] = useState({
    application: {
      creditCommittee: null,
      lastCreditCommitteeDate: null,
      bankLimit: {
        maxLimit: "",
        creditHistorySince: "",
        credits: { active: "", debt: "" },
        overdrafts: { active: "", debt: "" },
        leasing: { active: "", debt: "" },
        factoring: { active: "", debt: "" },
        guarantees: { active: "", debt: "" },
        individuals: { active: "", debt: "" }
      },
      creditHistoryYear: "",
      applicationEssence: "",
      applicationAmount: "",
      applicationCurrency: "",
      comments: "",
      businessEssence: "",
      preparedBy: "",
      checkedBy: "",
      creditCommitteeExpert: "",
      creditRequest: [
        {
          product: "",
          amount: "",
          currency: "",
          rate: "",
          term: "",
          purpose: "",
          collateral: ""
        }
      ],
      salesDynamics: "",
      customerBaseQualityAndDiversification: "",
      financialStability: "",
      liquidityAndPaymentDiscipline: "",
      businessProfitability: "",
      environmentalRisks: "",
      socialRisks: ""
    },
    generalInfo: {
      shortName: "",
      accountant: "",
      bankAffiliated: "",
      companiesInfo: [],
      businessLocations: [],
      businessLocationComment: ""
    },
    loans: {
      companyTakesLoan: false,
      companyTakesLoanSince: null,
      loansServicingNotes: "",
      currentSdmLoans: [],
      otherLoans: [],
      otherLoansDetails: ""
    },
    deposits: {
      guarantors: []
    },
    creditPolicy: {
      creditInspector: "",
      controller: "",
      mainBusinessRisks: "",
      argumentsFor: "",
      mainThesis: "",
      limitDynamics: {},
      increaseLimitReasons: "",
      increaseLimitConditions: "",
      collateralAndLtv: "",
      guarantees: "",
      interestRates: "",
      tenorsAndRepayment: "",
      monitoring: "",
      covenants: "",
      otherBanksRelations: "",
      otherConditions: ""
    },
    conclusion: {
      generalSummary: ""
    }
  });

  // Load data function
  const loadData = async (showLoading = true) => {
    const LoadingKey = getRandomKey();
    try {
      if (showLoading) {
        setLoading(true);
        dispatch(AppActions.startLoading(LoadingKey));
      }

      const data = await getJsonViaWebSocket(
        `/api/sdm/ws/client/${clientId}/credit-opinion/data`,
        {}
      );

      if (data) {
        setFormData(data);
      }

      if (showLoading) {
        setLoading(false);
        dispatch(AppActions.endLoading(LoadingKey));
      }
    } catch (error) {
      console.error("Error fetching SDM credit opinion data:", error);
      if (showLoading) {
        setLoading(false);
        dispatch(AppActions.endLoading(LoadingKey));
      }
    }
  };

  // Load activity types
  useEffect(() => {
    const loadActivityTypes = async () => {
      try {
        const response = await getAPI().request({
          url: '/api/sdm/activity-types',
          method: 'get'
        });

        if (response.data) {
          setActivityTypes(response.data);
        }
      } catch (error) {
        console.error("Error loading activity types:", error);
      }
    };

    loadActivityTypes();
  }, []);

  // Load data on modal open using WebSocket
  useEffect(() => {
    loadData();
  }, [clientId, dispatch]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Save handler
  const handleSave = async () => {
    const LoadingKey = getRandomKey();
    try {
      dispatch(AppActions.startLoading(LoadingKey));

      const response = await getAPI().request({
        url: `/api/client/${clientId}/credit-opinion/sdm/data`,
        method: "put",
        data: formData,
      });

      if (response.success) {
        console.log("Changes saved successfully");
      }
      dispatch(AppActions.endLoading(LoadingKey));
    } catch (error) {
      console.error("Error saving changes:", error);
      dispatch(AppActions.endLoading(LoadingKey));
    }
  };

  // Recalculate handler using WebSocket
  const handleRecalculate = async () => {
    const LoadingKey = getRandomKey();
    try {
      dispatch(AppActions.startLoading(LoadingKey));

      const data = await getJsonViaWebSocket(
        `/api/sdm/ws/client/${clientId}/credit-opinion/recalculate`,
        {}
      );

      if (data) {
        setFormData({
          ...formData,
          ...data
        });
        console.log("Data recalculated successfully");
      }
      dispatch(AppActions.endLoading(LoadingKey));
    } catch (error) {
      console.error("Error recalculating data:", error);
      dispatch(AppActions.endLoading(LoadingKey));
    }
  };

  // Excel export handler
  const handleDownloadFile = async () => {
    try {
      await handleSave();

      const fileSettings = { name: `credit_opinion_SDM_${clientId}.xlsm` };
      const blob = await getAPI().request({
        url: `/api/client/${clientId}/credit-opinion/sdm/file`,
        method: "get",
        responseType: 'blob',
        _fileSettings: fileSettings,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileSettings.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // Update form data helper
  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const nextKey = keys[i + 1];

        // Check if next key is array index
        if (!isNaN(nextKey)) {
          if (!current[key]) current[key] = [];
          current = current[key];
        } else {
          if (!current[key]) current[key] = {};
          current = current[key];
        }
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Build tabs array
const tabs = [
  { label: "Кредитная политика", key: "creditPolicy" },
  { label: "Заявка", key: "application" },
  { label: "Общая информация", key: "general" },
  ...(formData.generalInfo.companiesInfo || []).map((company, index) => ({
    label: company.shortName || `Компания ${index + 1}`,
    key: `conclusion-company-${index}`,
    companyIndex: index,
    isConclusion: true
  })),
  { label: "Обороты", key: "turnover" },
  { label: "Кредиты", key: "loans" },
  { label: "Залоги", key: "deposits" }
];

  if (loading) {
    return (
      <ModalWrapper
        maxWidth={"1200px"}
        open={true}
        handleClose={handleClose}
        btnTxt={"Close"}
      >
        <Box sx={{ padding: "40px", textAlign: "center" }}>Loading...</Box>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      maxWidth={"1500px"}
      open={true}
      handleClose={handleClose}
      btnTxt={"Close"}
    >
      {/* Flex container for fixed header/tabs and scrollable content */}
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(90vh - 60px)", // 90vh minus padding from ModalWrapper
        margin: "-30px", // Compensate for ModalWrapper padding
        padding: 0
      }}>
        {/* Header - fixed */}
        <Box sx={{ flexShrink: 0, padding: "30px 30px 0px" }}>
          <ModalTittle fontSize={"20px"} children2={`Кредитное заключение ${clientName || ""}`} marginBottom={"10px"} />
        </Box>

        {/* Tabs - fixed */}
        {tabs.length > 0 && (
          <Box sx={{
            borderBottom: "1px solid #E1E6F1",
            padding: "0 30px",
            flexShrink: 0,
            "& .MuiTabs-indicator": {
              backgroundColor: "#354659",
            },
            "& .MuiTab-root": {
              color: "#54606F",
              fontWeight: 500,
              fontSize: "16px",
              textTransform: "none",
              "&.Mui-selected": {
                color: "#1A1E24",
                fontWeight: 600,
              },
              "&:hover": {
                color: "#1A1E24",
              },
            },
          }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.key}
                  label={tab.label}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Tab content - scrollable */}
        <Box sx={{
          flex: 1,
          overflowY: "scroll",
          padding: "20px 30px 0",
          minHeight: 0, // Important for flex scrolling
          // Webkit scrollbar styles
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#555',
            },
          },
          // Firefox scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
        }}>
          {tabs[selectedTab]?.key === "creditPolicy" ? (
            <CreditPolicyTab
              data={formData}
              onUpdate={updateFormData}
            />
            ) : tabs[selectedTab]?.key === "application" ? (
            <ApplicationTab
              data={formData}
              onUpdate={updateFormData}
            />
            ) : tabs[selectedTab]?.key === "general" ? (
            <GeneralInfoTab
              data={formData}
              onUpdate={updateFormData}
            />
            ) : tabs[selectedTab]?.key === "turnover" ? (
            <TurnoverTab
              data={formData}
              onUpdate={updateFormData}
            />
            ) : tabs[selectedTab]?.key === "loans" ? (
            <LoansTab
              data={formData}
              onUpdate={updateFormData}
            />
            ) : tabs[selectedTab]?.key === "deposits" ? (
            <DepositsTab
              data={formData}
              onUpdate={updateFormData}
            />
            ) : tabs[selectedTab]?.isConclusion ? (
            <CompanyTab
              companyData={formData.generalInfo.companiesInfo[tabs[selectedTab].companyIndex]}
              companyIndex={tabs[selectedTab].companyIndex}
              onUpdate={updateFormData}
              clientId={clientId}
              onDataRefresh={() => loadData(false)}
              activityTypes={activityTypes}
              onSave={handleSave}
            />
            ) : (
            <Box sx={{ padding: "20px" }}>
              {}
            </Box>
            )}
        </Box>

        {/* Bottom buttons - fixed */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          padding: "16px 30px 30px",
          flexShrink: 0,
          borderTop: "1px solid #E1E6F1"
        }}>
          <div style={{ marginTop: '-20px' }}>
            <ButtonSave
              onClick={handleRecalculate}
              marginTop={0}
              marginBottom={0}
            >
              Пересчитать
            </ButtonSave>
          </div>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <div style={{ marginTop: '-20px' }}>
              <ButtonSave
                onClick={handleSave}
                marginTop={0}
                marginBottom={0}
              >
                buttons.Save
              </ButtonSave>
            </div>

            <ButtonBB
              onClick={handleDownloadFile}
              className="ButtonAddNew"
              style={{ padding: '15px 20px' }}
            >
              {t("buttons.ExportToExcel")}
            </ButtonBB>
          </Box>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default SDMClientCreditOpinion;
